import React, { useCallback, useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import styled from 'styled-components';
import ReportFinalize from '../ReportFinalize';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBadgeCheck, faMessages } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { Report } from 'shared/components/WelcomeBack/utils';
import { isReportFinalized, isReportInterim } from '../ReportFinalize/utils';
import LockReport from '../ReportFinalize/LockReport';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import FeedbackModal from './FeedbackModal';
import { getRatingById } from './utils';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import { useProgramsAndReports } from 'shared/hooks/useProgramsAndReports';
import ReportBuilderSharingModel from 'shared/account/pages/reportSharing/ReportBuilderSharingModel';
import { isBbUserView } from 'shared/utils/partnerUser';

const StyledDiv = styled.div`
  max-width: 530px;
  text-align: center;
  margin: 80px auto;
  .icon {
    margin-bottom: ${SPACERS.extraLarge};
  }
  .body-text {
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.extraLarge};
  }
  .feedback-button {
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.large};
    .feedback-icon {
      margin-right: ${SPACERS.tiny};
    }
  }
  .submit-button {
    margin-left: auto;
    margin-right: auto;
  }
`;

const ReportSubmittedRender: React.FC<{
  report: Report;
  isDebug: boolean;
  isThirdParty: boolean;
  showRatePopup?: boolean;
}> = ({ report, isDebug, showRatePopup = false, isThirdParty }) => {
  const { push } = useHistory();
  const isBbPartner = isBbUserView();
  const reportVisibility = useFeatureFlags('reportVisibility');
  const { path: reportsPath } = useProgramsAndReports();
  const [showFeedback, setShownFeedback] = useState<boolean>(false);
  const [feedbackDisable, setFeedbackDisable] = useState<boolean>(false);
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rating = getRatingById(report.dbId);
    if (rating) {
      setFeedbackDisable(true);
      return;
    }
    if (showRatePopup) {
      window.setTimeout(() => {
        setShownFeedback(true);
      }, 4000);
    }
  }, [showRatePopup, report.dbId]);

  const handleFeedbackDisable = useCallback(() => {
    setFeedbackDisable(true);
  }, [setFeedbackDisable]);

  const handleOpen = useCallback(() => {
    setShownFeedback(true);
  }, [setShownFeedback]);
  const handleClose = useCallback(() => {
    setShownFeedback(false);
  }, [setShownFeedback]);

  useEffect(() => {
    if (!ref || !ref.current) return;
    const parent = ref.current.parentElement?.parentElement;
    setWidth(parent?.clientWidth || 0);
    setHeight(parent?.clientHeight || 0);
  }, [ref, setWidth, setHeight]);

  const onClick = useCallback(() => {
    push(reportsPath);
  }, [push, reportsPath]);

  return (
    <>
      {reportVisibility && <ReportBuilderSharingModel report={report} />}
      <StyledDiv ref={ref}>
        <FontAwesomeIcon icon={faBadgeCheck} size="6x" className="icon" />
        <TrueImpactText variant="heading-page">
          Thanks for submitting your report!
        </TrueImpactText>
        <Confetti
          width={width}
          height={height}
          numberOfPieces={1000}
          recycle={false}
        />
        <TrueImpactText className="body-text">
          We truly appreciate the time and effort that goes into this. We will
          let you know once your report feedback is ready for your review.
        </TrueImpactText>
        {isThirdParty && !isBbPartner && (
          <TrueImpactText>
            Thank you for submitting your report to Charity Navigator! Your
            report will be rated by CN.
          </TrueImpactText>
        )}
        {isBbPartner && (
          <TrueImpactText>
            Your report has been submitted successfully.
          </TrueImpactText>
        )}
        {!isThirdParty && !isBbPartner && (
          <>
            <TrueImpactButton
              disabled={feedbackDisable}
              backgroundColor="success"
              className="feedback-button"
              backgroundColorHover="brandSecondary"
              colorHover="blackBase"
              onClick={handleOpen}
            >
              <FontAwesomeIcon icon={faMessages} className="feedback-icon" />{' '}
              {feedbackDisable ? 'Feedback Provided!' : 'Feedback'}
            </TrueImpactButton>
            <TrueImpactButton className="submit-button" onClick={onClick}>
              Take Me to the Report Dashboard
            </TrueImpactButton>
          </>
        )}
      </StyledDiv>
      <FeedbackModal
        isDebug={isDebug}
        className="feedback-modal-content"
        open={showFeedback && !feedbackDisable}
        reportId={report.dbId}
        handleClose={handleClose}
        handleDisableFeedback={handleFeedbackDisable}
      />
    </>
  );
};

interface ReportSubmittedProps {
  report: Report;
  isDebug: boolean;
  answers: Answer[];
  isLoading: boolean;
  handlePressSection: (sectionId: number) => unknown;
  setIsReportInprogress: (show: boolean) => unknown;
  handlePressOption: (secId: number, optId: number) => unknown;
  showRatePopup?: boolean;
}

const ReportSubmitted: React.FC<ReportSubmittedProps> = ({
  report,
  answers,
  isDebug,
  isLoading,
  handlePressOption,
  handlePressSection,
  setIsReportInprogress,
  showRatePopup,
}) => {
  const isBbPartner = isBbUserView();
  const isThirdParty = report.cnOptedIn || isBbPartner;
  if (!report?.stage || !report?.status || !answers) {
    return <TrueImpactLoading />;
  }

  const isInterim = isReportInterim(report.stage);

  if (isInterim || isReportFinalized(report.stage)) {
    if (report.status === 'Finalized') {
      return (
        <LockReport
          report={report}
          answers={answers}
          isLoading={isLoading}
          handlePressOption={handlePressOption}
          handlePressSection={handlePressSection}
          setIsReportInprogress={setIsReportInprogress}
        />
      );
    }
    if (
      report.status === 'Final Updates Needed' ||
      report.status === 'Interim Updates Needed'
    ) {
      return (
        <ReportFinalize
          report={report}
          answers={answers}
          isInterim={isInterim}
          isLoading={isLoading}
          isThirdParty={isThirdParty}
          handlePressOption={handlePressOption}
          handlePressSection={handlePressSection}
          setIsReportInprogress={setIsReportInprogress}
        />
      );
    }
  }

  return (
    <ReportSubmittedRender
      report={report}
      isDebug={isDebug}
      isThirdParty={isThirdParty}
      showRatePopup={showRatePopup}
    />
  );
};

export default ReportSubmitted;
